import type { AeaExerciseModel, BandUnit, TeacherTestDetails, TestDetailsWord, UnitCard, UserOwnCard } from "p6m-p6u";
import type { MakePxpRequest } from "../context/AppGeneralContext";
import { processCardsForUnitResponse } from "./networkHelper";
import { translateWordFromQuestionToModel } from "./TestCreation";
import { fetchUserUnitsWithCards } from "./UserExercises";
import { getCardsForUnit } from "../networking/tests";

export enum ActivitySourceKinds {
    BOOK = "BOOK",
    OWN = "OWN",
}

/** Collects the words of every exercise, deduped by word id, in test order. */
export const collectTestWords = (test: TeacherTestDetails, filter?: ActivitySourceKinds): TestDetailsWord[] => {
    const wordsById = new Map<string, TestDetailsWord>();

    test.content?.forEach((exercise) => {
        exercise.selectedWords?.forEach((word) => {
            if (!("id" in word) || !word.id || wordsById.has(word.id)) {
                return;
            }
            const isOwnWord = !!(word as TestDetailsWord).isUserCard;
            const isFilteredOut =
                (filter === ActivitySourceKinds.BOOK && isOwnWord) ||
                (filter === ActivitySourceKinds.OWN && !isOwnWord);
            if (isFilteredOut) {
                return;
            }
            wordsById.set(word.id, word as TestDetailsWord);
        });
    });

    return Array.from(wordsById.values());
};

/**
 * A saved test keeps only a reduced copy of each book word (no uuid / article id), so book words
 * are re-fetched from their units and words no longer there are dropped. Own words carry
 * everything needed and are translated directly.
 */
export const loadCardsFromTest = async (
    test: TeacherTestDetails,
    filter?: ActivitySourceKinds
): Promise<Array<UnitCard | UserOwnCard>> => {
    const words = collectTestWords(test, filter);
    const bookUnitIds = Array.from(
        new Set(words.filter((word) => !word.isUserCard && word.unit_id).map((word) => String(word.unit_id)))
    );

    const unitCards = await Promise.all(
        bookUnitIds.map(async (unitId) => processCardsForUnitResponse(unitId, await getCardsForUnit(unitId)))
    );
    const bookCardById: Record<string, UnitCard> = {};
    unitCards.flat().forEach((card) => {
        if (card.id) bookCardById[card.id] = card;
    });

    return words.flatMap((word) => {
        if (word.isUserCard) return [translateWordFromQuestionToModel(word)];
        const card = bookCardById[word.id!];
        return card ? [card] : [];
    });
};

export const getTrainingCardIds = (training: AeaExerciseModel): string[] => {
    return (training.cardIds ?? []).map((c) => c.id).filter((id): id is string => !!id);
};

/**
 * Cards fetched by id come raw from the CDB; the vocab screens expect them shaped like the
 * per-unit loader's output (unit id stamped, text sanitized, gap sentence) — the collapsed
 * unit counter in particular matches cards to units via `unitId`.
 */
export const normalizeBookCards = (cards: UnitCard[], units: BandUnit[]): UnitCard[] => {
    const unitIdBySetUuid: Record<string, string> = {};
    units.forEach((unit) => {
        if (unit.uuid && unit.id) unitIdBySetUuid[unit.uuid] = unit.id;
    });

    return cards.flatMap((card) => {
        const unitId = card.item_set_uuid ? unitIdBySetUuid[card.item_set_uuid] : undefined;
        const [processed] = processCardsForUnitResponse(unitId ?? "", { items: [card] });
        if (!processed) return [];
        if (!unitId) delete processed.unitId;
        return [processed];
    });
};

export const fetchOwnContentCards = async (
    makePxpRequest: MakePxpRequest,
    subjectUuid: string,
    includedCardIds: Set<string>
): Promise<UserOwnCard[]> => {
    const unitsWithCards = await fetchUserUnitsWithCards(makePxpRequest, subjectUuid);
    return unitsWithCards.flatMap(({ cards }) => cards).filter((card) => card.id && includedCardIds.has(card.id));
};
